import dayjs, { Dayjs } from "dayjs";
import { ArrowSmallLineUpIcon } from "../../assets";
import { IUser } from "../../models";
import styles from "./styles.module.scss";
import { useQuery } from "@tanstack/react-query";
import { getDailyProgress } from "../../services/users.service";
import { useMemo, useState } from "react";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryCursorContainer,
  VictoryStack,
} from "victory";
import { COLORS } from "../../constants";
import React from "react";
import { Button, Legend } from "../../components";
import { formatNumberToLocaleString } from "../../utils/methods";
import { PulseLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { first, last } from "lodash";

type MonthlyIntakeChartProps = {
  user: IUser;
  date: Dayjs;
};

const getDaysOfMonth = (fromDate = new Date()) => {
  const currentWeek = dayjs(fromDate);
  let start = dayjs(currentWeek).utc().startOf("months");
  const end = dayjs(currentWeek).utc().endOf("months");

  const days: string[] = [];

  while (!end.isBefore(start, "day")) {
    days.push(start.toISOString().split("T")[0]);
    start = start.add(1, "day");
  }

  return days;
};

type ChartData = {
  x: string;
  y: number;
  y0?: number;
  date: string;
  weight?: number;
  isWeightLogged?: boolean;
  isNonCompletedDay?: boolean;
  logId?: number;
  index?: number;
  isSkipped?: boolean;
};

const interpolate = ({
  inputRange,
  outputRange,
}: {
  inputRange: [number, number];
  outputRange: [number, number];
}) => {
  const [inputMin, inputMax] = inputRange;
  const [outputMin, outputMax] = outputRange;

  return (inputValue: number) => {
    const inputRange = inputMax - inputMin;
    const outputRange = outputMax - outputMin;

    const inputValueScaled = (inputValue - inputMin) / inputRange;

    return outputMin + inputValueScaled * outputRange;
  };
};

const MonthlyIntakeChart: React.FC<MonthlyIntakeChartProps> = ({
  user,
  date,
}) => {
  const fromDate = date.startOf("month").format("YYYY-MM-DD");
  const toDate = date.endOf("month").format("YYYY-MM-DD");

  const [selectedChartType, setSelectedChartType] = useState<
    "accumulative" | "daily"
  >("accumulative");

  const [activePoint, setActivePoint] = useState<string | null>(null);
  const navigation = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: ["daily-progress", user?.id, fromDate, toDate],
    queryFn: () => {
      return getDailyProgress(user?.id || 0, fromDate, toDate);
    },
  });

  const daysInWeek = useMemo(() => {
    return getDaysOfMonth(date?.toDate());
  }, [date]);

  const chartData = useMemo(() => {
    let maxValue = 0;

    const intakeData: ChartData[] = [];
    const xAxisLabels: string[] = [];

    const currentData =
      selectedChartType === "accumulative" ? data?.accumulated : data?.progress;

    currentData?.forEach((item) => {
      const max = item.intake;

      if (max > maxValue) {
        maxValue = max;
      }
    });

    maxValue = maxValue * 1.15;

    daysInWeek.forEach((date, index) => {
      xAxisLabels.push(dayjs.utc(date).format("MMM DD"));

      if (
        dayjs.utc(date).isBefore(dayjs.utc(user?.onboardingAt), "day") ||
        dayjs.utc(date).isAfter(dayjs().utc(true), "days")
      ) {
        intakeData.push({
          x: date,
          date,
          y: 0,
          y0: 0,
          index: index + 1,
        });

        return;
      }

      const statsForDate = currentData?.find((item) => item.date === date);

      if (statsForDate) {
        const currentWeight = statsForDate?.weight || 0;

        const isDayInGoal = !!statsForDate?.userGoalId;

        const isNonCompletedDay =
          statsForDate?.date &&
          !statsForDate?.isDayCompleted &&
          dayjs.utc(statsForDate?.date).isBefore(dayjs().utc(true), "day");

        const isSkipped =
          statsForDate?.completionType === "Skip" ||
          statsForDate?.completionType === "Auto-Skip";

        intakeData.push({
          date: statsForDate.date,
          x: statsForDate.date,
          y: statsForDate.intake,
          y0: 0,
          index: index + 1,
          isNonCompletedDay: !!isNonCompletedDay,
          isSkipped,
        });
      }
    });

    return {
      maxValue,
      intakeData,
      xAxisLabels,
    };
  }, [data, daysInWeek, user, selectedChartType]);

  const weightLoss = useMemo(() => {
    if (data?.progress) {
      return (
        data?.progress?.[0]?.weight -
        data?.progress?.[data?.progress?.length - 1]?.weight
      );
    }
    return 0;
  }, [data]);

  const activeValue = useMemo(() => {
    if (selectedChartType === "accumulative") {
      return data?.accumulated?.find((item) => item.date === activePoint);
    } else {
      return data?.progress?.find((item) => item.date === activePoint);
    }
  }, [activePoint, selectedChartType]);

  const leftSubValue = activePoint
    ? activeValue?.intake || 0
    : selectedChartType === "accumulative"
    ? data?.deficitSum || 0
    : data?.deficitAvg || 0;

  const avgAccumulatedIntake = useMemo(() => {
    if (data) {
      const accumulatedIntake = data?.progress?.reduce(
        (acc, item) => acc + item.intake,
        0
      );
      const averageIntake = accumulatedIntake / data?.progress?.length;

      return {
        averageIntake,
        accumulatedIntake,
      };
    }
    return {
      averageIntake: 0,
      accumulatedIntake: 0,
    };
  }, [data, selectedChartType]);

  const leftValue = activePoint
    ? activeValue?.intake || 0
    : selectedChartType === "accumulative"
    ? avgAccumulatedIntake.accumulatedIntake
    : avgAccumulatedIntake.averageIntake;

  const findClosestPointSorted = (data: any[], value: number) => {
    if (value === null) return null;
    const start = first(data).index;
    const range = last(data).index - start;
    const index = Math.round(((value - start) / range) * (data.length - 1));
    return data[index];
  };

  const handleCursorChange = (value: number) => {
    setActivePoint(findClosestPointSorted(chartData.intakeData, value)?.date);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.title}>Calorie In</p>
        <div
          onClick={() => navigation(`/calorie-deficit-data/${user?.id}`)}
          className={styles.details}
        >
          <p>Details</p>
          <ArrowSmallLineUpIcon className={styles.rightArrow} />
        </div>
      </div>

      {isPending ? (
        <div className={styles.loaderContainer}>
          <PulseLoader color="rgba(97, 42, 255, 1)" />
        </div>
      ) : (
        <div className={styles.chartContainer}>
          <div className={styles.rowContainer}>
            <Button
              onClick={() => setSelectedChartType("accumulative")}
              size="x-small"
              title="Cumulative"
              styleType={
                selectedChartType === "accumulative" ? "filled" : "inactive"
              }
            />
            <Button
              onClick={() => setSelectedChartType("daily")}
              size="x-small"
              title="Daily"
              styleType={selectedChartType === "daily" ? "filled" : "inactive"}
            />
          </div>
          <div className={styles.headerValues}>
            <div style={{ height: 72 }} className={styles.block}>
              {activeValue?.completionType === "Skip" ||
              activeValue?.completionType === "Auto-Skip" ? (
                <>
                  <p className={styles.weight}></p>
                  <p className={styles.calories}>Skipped</p>
                </>
              ) : (
                <>
                  <p className={styles.weight}>
                    {formatNumberToLocaleString(leftValue)} <span>{"cal"}</span>
                  </p>

                  <p className={styles.title}>
                    {selectedChartType === "accumulative"
                      ? "Cumulative Intake "
                      : activePoint
                      ? "Intake"
                      : "Average Intake"}{" "}
                  </p>
                </>
              )}
            </div>
            <div className={styles.block}></div>
          </div>
          <VictoryChart
            maxDomain={{ y: chartData.maxValue }}
            padding={{ left: 0, top: 15, bottom: 15, right: 0 }}
            domainPadding={{ x: [5, 5] }}
            containerComponent={
              <VictoryCursorContainer
                disable={false}
                cursorDimension="x"
                onCursorChange={(s, d) => handleCursorChange(s as number)}
              />
            }
          >
            <defs>
              <pattern
                id={"stripes-green"}
                viewBox="0 0 8 8"
                width="8"
                height="8"
                patternUnits="userSpaceOnUse"
              >
                <polygon points="0,0 4,0 0,4" fill={COLORS.greenOpacity20} />
                <polygon
                  points="0,8 8,0 8,4 4,8"
                  fill={COLORS.greenOpacity20}
                />
                <polygon points="0,4 0,8 8,0 4,0" fill={COLORS.green} />
                <polygon points="4,8 8,8 8,4" fill={COLORS.green} />
              </pattern>
              <pattern
                id={"stripes-gray"}
                viewBox="0 0 8 8"
                width="8"
                height="8"
                patternUnits="userSpaceOnUse"
              >
                <polygon points="0,0 4,0 0,4" fill={COLORS.darkGrayOpacity20} />
                <polygon
                  points="0,8 8,0 8,4 4,8"
                  fill={COLORS.darkGrayOpacity20}
                />
                <polygon points="0,4 0,8 8,0 4,0" fill={COLORS.mediumGray} />
                <polygon points="4,8 8,8 8,4" fill={COLORS.mediumGray} />
              </pattern>
            </defs>
            <VictoryAxis
              dependentAxis={false}
              tickFormat={() => {
                return "";
              }}
              style={{
                axis: {
                  stroke: COLORS.lightGray,
                },
              }}
            />

            <VictoryStack>
              <VictoryBar
                barWidth={5}
                style={{
                  data: {
                    fill: ({ datum }) => {
                      if (datum?.isNonCompletedDay || datum?.isSkipped) {
                        return COLORS.darkGrayOpacity20;
                      }
                      return COLORS.blue;
                    },
                    // fillOpacity: ({ active }) => {
                    // return activePoint && !active ? 0.2 : 1;
                    // },
                  },
                }}
                cornerRadius={{
                  top: () => 3,
                }}
                data={chartData.intakeData}
              />
            </VictoryStack>
          </VictoryChart>
          <div className={styles.xAxisContainer}>
            <p className={styles.xAxisText}>{chartData?.xAxisLabels?.[0]}</p>

            <p className={styles.xAxisText}>
              {chartData?.xAxisLabels?.[chartData?.xAxisLabels?.length - 1]}
            </p>
          </div>
          <div className={styles.footerChart}>
            <Legend
              label={"Intake"}
              colorOpacity={COLORS.blue}
              color={COLORS.blue}
              description="Your calorie deficit goal."
              variant="border"
            />

            <Legend
              label={"Goal"}
              description="Your calorie deficit goal."
              variant="line"
              color={COLORS.purple}
              colorOpacity={COLORS.purple}
            />

            <Legend
              label={"Weight"}
              description=""
              variant="line"
              color={COLORS.orange}
              colorOpacity={COLORS.orange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyIntakeChart;
